import { useState } from 'react';
import { Barras } from '../graficos/Barras.jsx';
import { BarrasHorizontais } from '../graficos/BarrasHorizontais.jsx';
import { Donut } from '../graficos/Donut.jsx';
import { Linha } from '../graficos/Linha.jsx';
import { MapaDeCalor } from '../graficos/MapaDeCalor.jsx';
import { AbasDeModal, CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { EsqueletoDeCard, EsqueletoDeTabela, Erro } from '../ui/estados.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  Avatar,
  Botao,
  BotaoDeIcone,
  Campo,
  Card,
  Escolhas,
  Feed,
  Formulario,
  ItemDeFeed,
  Kpi,
  LinhaDePills,
  Mini,
  Nota,
  Quem,
  Segmentado,
  Status,
  Tabela,
  Tag,
  Vazio,
} from '../ui/primitivos.jsx';

/**
 * A página de referência do design system — tarefa 13 do documento 05.
 *
 * Ela existe para ser CONFERIDA lado a lado com o protótipo. Não é
 * documentação decorativa: é o instrumento que detecta divergência
 * visual antes que ela se espalhe por vinte telas. Quando um módulo
 * precisar de um componente novo, ele entra aqui primeiro.
 *
 * Fica em `/referencia`, fora do menu. Não é tela de produto.
 */
export function Referencia() {
  const avisar = useToast();
  const [modal, setModal] = useState<'nenhum' | 'simples' | 'abas'>('nenhum');
  const [periodo, setPeriodo] = useState<'semana' | 'mes'>('semana');
  const [modalidade, setModalidade] = useState<'futvolei' | 'volei'>('futvolei');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Referência do design system</h1>
          <p>Confira lado a lado com o protótipo em Docs/arena-alpha-prototipo.html</p>
        </div>
        <div className="head-actions">
          <Segmentado
            aria-label="Período"
            valor={periodo}
            onChange={setPeriodo}
            opcoes={[
              { valor: 'semana', rotulo: 'Semana' },
              { valor: 'mes', rotulo: 'Mês' },
            ]}
          />
          <Botao icone="plus" onClick={() => avisar('Foi só um exemplo.')}>
            Ação primária
          </Botao>
        </div>
      </div>

      <Nota>
        O <b>modo apresentação</b> no rodapé da sidebar esconde estas notas. É o mesmo comportamento
        do protótipo.
      </Nota>

      {/* ----------------------------------------------------------- */}
      <div className="grid g4">
        <Kpi
          valor="R$ 87.740"
          rotulo="Receita do mês"
          icone="money"
          tendencia="alta"
          variacao="12%"
        />
        <Kpi
          valor="78%"
          rotulo="Ocupação no horário nobre"
          icone="cal"
          tendencia="baixa"
          variacao="4%"
        />
        <Kpi
          valor="412"
          rotulo="Alunos ativos"
          icone="users"
          tendencia="estavel"
          variacao="estável"
        />
        <Kpi valor="R$ 6.910" rotulo="Bar no mês" icone="tag" tendencia="alta" variacao="8%" />
      </div>

      {/* ----------------------------------------------------------- */}
      <div className="grid g-2-1">
        <Card titulo="Tabela" dica="com pills de situação">
          <Tabela colunas={['Aluno', 'Plano', 'Situação', 'Vencimento']}>
            {[
              {
                nome: 'Brenda Loureiro',
                plano: 'Futevôlei 2x',
                s: 'ok' as const,
                t: 'Em dia',
                v: '10/09',
              },
              {
                nome: 'Caio Duarte',
                plano: 'Vôlei 2x',
                s: 'atencao' as const,
                t: 'A vencer',
                v: '05/09',
              },
              {
                nome: 'Jéssica Prado',
                plano: 'Mensal livre',
                s: 'atrasada' as const,
                t: 'Vencida',
                v: '28/08',
              },
              { nome: 'Pedro Lima', plano: '—', s: 'neutra' as const, t: 'Cancelado', v: '—' },
            ].map((l) => (
              <tr key={l.nome}>
                <td>
                  <Quem nome={l.nome} detalhe="desde 03/2026" />
                </td>
                <td>{l.plano}</td>
                <td>
                  <Status situacao={l.s}>{l.t}</Status>
                </td>
                <td className="num">{l.v}</td>
              </tr>
            ))}
          </Tabela>
        </Card>

        <Card titulo="Feed" dica="linha com marca e coluna à direita">
          <Feed>
            <ItemDeFeed
              marca={
                <span className="sq" style={{ background: 'var(--quadra-1)' }}>
                  Q1
                </span>
              }
              titulo="Futevôlei intermediário"
              subtitulo="Prof. Dener · 8 de 10 alunos"
              direita={
                <>
                  <b className="num">19h</b>
                  <small>60 min</small>
                </>
              }
              onClick={() => avisar('Abriria a turma.')}
            />
            <ItemDeFeed
              marca={
                <span className="sq" style={{ background: 'var(--quadra-2)' }}>
                  Q2
                </span>
              }
              titulo="Vôlei iniciante"
              subtitulo="Prof. Marina · 10 de 10 alunos"
              direita={
                <>
                  <b className="num">19h</b>
                  <small>lotada</small>
                </>
              }
            />
            <ItemDeFeed
              marca={<Avatar nome="Camila Souza" />}
              titulo="Camila abriu a ficha de Brenda"
              subtitulo="leitura de dado pessoal"
              direita={<small>há 4 min</small>}
            />
          </Feed>
        </Card>
      </div>

      {/* ----------------------------------------------------------- */}
      <div className="grid g3">
        <Card titulo="Rosca" dica="donut">
          <Donut
            centro={{ valor: '30 h', rotulo: 'capacidade' }}
            fatias={[
              {
                nome: 'Turmas fixas',
                valor: 13,
                cor: 'var(--serie-1)',
                rotulo: '13,0 h',
                dica: '6 aulas na grade de hoje.<br><em>43% das horas</em>',
              },
              {
                nome: 'Locação avulsa',
                valor: 5.5,
                cor: 'var(--serie-2)',
                rotulo: '5,5 h',
                dica: '2 reservas confirmadas.<br><em>R$ 566 previstos</em>',
              },
              {
                nome: 'Experimental',
                valor: 1.9,
                cor: 'var(--serie-4)',
                rotulo: '1,9 h',
                dica: '1 aula vinda do site.',
              },
              {
                nome: 'Livre',
                valor: 9.6,
                cor: 'var(--panel-3)',
                rotulo: '9,6 h',
                dica: 'Capacidade parada.<br><em>R$ 989 não faturados</em>',
              },
            ]}
          />
        </Card>

        <Card titulo="Barras verticais" dica="com pares">
          <Barras
            cores={['var(--serie-1)', 'var(--serie-5)']}
            legenda={[
              { rotulo: 'Presenças', cor: 'var(--serie-1)' },
              { rotulo: 'Faltas', cor: 'var(--serie-5)' },
            ]}
            colunas={[
              { x: 'S1', valores: [62, 8], dicas: ['62 presenças', '8 faltas · 11%'] },
              { x: 'S2', valores: [58, 12], dicas: ['58 presenças', '12 faltas · 17%'] },
              { x: 'S3', valores: [64, 6], dicas: ['64 presenças', '6 faltas · 9%'] },
              { x: 'S4', valores: [60, 10], dicas: ['60 presenças', '10 faltas · 14%'] },
              {
                x: 'S5',
                valores: [66, 4],
                dicas: ['66 presenças · melhor semana', '4 faltas · 6%'],
              },
            ]}
          />
        </Card>

        <Card titulo="Barras horizontais" dica="ranking">
          <BarrasHorizontais
            itens={[
              {
                nome: 'Água 500 ml',
                valor: 186,
                rotulo: '186 un',
                cor: 'var(--serie-2)',
                dica: 'R$ 930 no mês.<br><em>margem de 72%</em>',
              },
              {
                nome: 'Coca-Cola Zero',
                valor: 142,
                rotulo: '142 un',
                cor: 'var(--serie-1)',
                dica: 'R$ 994 no mês.',
              },
              {
                nome: 'Isotônico',
                valor: 98,
                rotulo: '98 un',
                cor: 'var(--serie-3)',
                dica: 'R$ 882 no mês.',
              },
              {
                nome: 'Açaí 300 ml',
                valor: 74,
                rotulo: '74 un',
                cor: 'var(--serie-4)',
                dica: 'Maior receita por unidade.',
              },
            ]}
          />
        </Card>
      </div>

      {/* ----------------------------------------------------------- */}
      <div className="grid g-1-2">
        <Card titulo="Mapa de calor" dica="ocupação por faixa">
          <MapaDeCalor
            faixas={[
              { faixa: '17', ocupacao: [40, 62, 38, 60, 44, 88, 30] },
              { faixa: '18', ocupacao: [70, 92, 68, 94, 72, 96, 40] },
              { faixa: '19', ocupacao: [82, 98, 80, 97, 78, 90, 34] },
              { faixa: '20', ocupacao: [66, 84, 62, 86, 58, 62, 22] },
              { faixa: '21', ocupacao: [30, 48, 28, 46, 24, 30, 12] },
            ]}
            dicaDe={(_dia, _faixa, p) =>
              `Ocupação de <em>${p}%</em><br>${((p / 100) * 10).toFixed(1)} h de 10 h` +
              (p >= 95
                ? '<br><em>lista de espera ativa</em>'
                : p < 25
                  ? '<br><em>candidato a promoção</em>'
                  : '')
            }
          />
        </Card>

        <Card titulo="Linha de projeção" dica="realista, conservador e custo fixo">
          <Linha
            rotulos={['Set', 'Out', 'Nov', 'Dez']}
            minimo={16000}
            maximo={38000}
            formatar={(v) => `R$ ${v.toLocaleString('pt-BR')}`}
            series={[
              { nome: 'realista', valores: [26320, 28100, 31450, 34200], cor: 'var(--serie-1)' },
              { nome: 'conservador', valores: [26320, 24800, 25600, 26900], cor: 'var(--serie-2)' },
              {
                nome: 'custo fixo',
                valores: [21000, 21000, 22400, 22400],
                cor: 'var(--dim)',
                referencia: true,
              },
            ]}
          />
        </Card>
      </div>

      {/* ----------------------------------------------------------- */}
      <div className="grid g3">
        <Card titulo="Formulário">
          <Formulario onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Nome" ajuda="Como aparece na ficha.">
              <input defaultValue="Brenda Loureiro" />
            </Campo>
            <Campo rotulo="Telefone" erro="Informe DDD e 9 dígitos.">
              <input defaultValue="11 9" />
            </Campo>
            <Campo rotulo="Modalidade">
              <Escolhas
                aria-label="Modalidade"
                valor={modalidade}
                onChange={setModalidade}
                opcoes={[
                  { valor: 'futvolei', rotulo: 'Futevôlei' },
                  { valor: 'volei', rotulo: 'Vôlei' },
                ]}
              />
            </Campo>
          </Formulario>
        </Card>

        <Card titulo="Blocos menores">
          <Mini
            itens={[
              { valor: '412', rotulo: 'ativos' },
              { valor: '18', rotulo: 'experimentais' },
              { valor: '7', rotulo: 'inadimplentes' },
            ]}
          />
          <div style={{ marginTop: 16 }}>
            <LinhaDePills>
              <Tag>terça 19h</Tag>
              <Tag>quinta 19h</Tag>
              <Tag>Quadra 1</Tag>
              <Tag>intermediário</Tag>
            </LinhaDePills>
          </div>
          <div style={{ display: 'flex', gap: 9, marginTop: 16, flexWrap: 'wrap' }}>
            <Botao>Primário</Botao>
            <Botao variante="fantasma">Fantasma</Botao>
            <Botao variante="contorno">Contorno</Botao>
            <Botao pequeno variante="contorno">
              Pequeno
            </Botao>
            <BotaoDeIcone icone="bell" aria-label="Alertas" comAviso />
          </div>
          <div style={{ display: 'flex', gap: 9, marginTop: 16, flexWrap: 'wrap' }}>
            <Botao variante="contorno" onClick={() => setModal('simples')}>
              Modal
            </Botao>
            <Botao variante="contorno" onClick={() => setModal('abas')}>
              Modal com abas
            </Botao>
            <Botao variante="contorno" onClick={() => avisar('Reserva confirmada.')}>
              Toast
            </Botao>
          </div>
        </Card>

        <Card titulo="Estados">
          <div style={{ display: 'grid', gap: 14 }}>
            <Vazio
              acao={
                <Botao pequeno icone="plus">
                  Nova reserva
                </Botao>
              }
            >
              Nenhuma reserva neste dia.
            </Vazio>
            <EsqueletoDeTabela linhas={3} colunas={3} />
            <Erro tipo="permissao" />
            <Erro tipo="rede" onTentarDeNovo={() => avisar('Tentaria de novo.')} />
          </div>
        </Card>
      </div>

      <div className="grid g3">
        <EsqueletoDeCard />
        <EsqueletoDeCard linhas={2} />
        <EsqueletoDeCard linhas={4} />
      </div>

      {/* ----------------------------------------------------------- */}
      <Modal
        aberto={modal === 'simples'}
        onFechar={() => setModal('nenhum')}
        titulo="Nova reserva"
        descricao="Quadra 1 · terça, 19h"
        rodape={
          <>
            <span className="left">Conflito verificado na gravação.</span>
            <Botao variante="fantasma" onClick={() => setModal('nenhum')}>
              Cancelar
            </Botao>
            <Botao
              onClick={() => {
                setModal('nenhum');
                avisar('Reserva confirmada.');
              }}
            >
              Confirmar
            </Botao>
          </>
        }
      >
        <CorpoDeModal>
          <Formulario colunas={2}>
            <Campo rotulo="Responsável">
              <input defaultValue="Breno Martins" />
            </Campo>
            <Campo rotulo="Pessoas">
              <input defaultValue="6" className="num" />
            </Campo>
          </Formulario>
        </CorpoDeModal>
      </Modal>

      <Modal
        aberto={modal === 'abas'}
        onFechar={() => setModal('nenhum')}
        titulo="Brenda Loureiro"
        descricao="Aluna desde março de 2026"
        largura="larga"
      >
        <AbasDeModal
          abas={[
            {
              chave: 'dados',
              rotulo: 'Dados',
              conteudo: <p className="sub">Cadastro, contato e responsável.</p>,
            },
            {
              chave: 'financeiro',
              rotulo: 'Financeiro',
              conteudo: <p className="sub">Faturas e pagamentos.</p>,
            },
            {
              chave: 'presencas',
              rotulo: 'Presenças',
              conteudo: <p className="sub">Frequência por turma.</p>,
            },
            {
              chave: 'lgpd',
              rotulo: 'LGPD',
              conteudo: (
                <Nota>
                  Aceito em 12/03/2026 às 19:41 · IP 187.xx.xx.xx · versão 1.2. Toda leitura desta
                  ficha é registrada em auditoria.
                </Nota>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
